import { useState } from 'react';
import { Button, Group, List, Modal, Stack, Text } from '@mantine/core';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';

type SharedFeedMatch = {
  identifier_type: 'FRN' | 'FINIO_FIRM_NAME';
  identifier: string;
  company_name: string;
};

type PendingConfirmation = {
  matches: SharedFeedMatch[];
  resolve: (confirmed: boolean) => void;
};

const IDENTIFIER_LABELS: Record<SharedFeedMatch['identifier_type'], string> = {
  FRN: 'FRN',
  FINIO_FIRM_NAME: 'FINIO firm name',
};

function getSharedFeedMatches(error: unknown): SharedFeedMatch[] | null {
  if (!ApiError.isApiError(ApiErrorCode.SHARED_FEED_IDENTIFIERS, error)) return null;
  const matches = error.detail?.matches;
  return Array.isArray(matches) ? (matches as SharedFeedMatch[]) : null;
}

/**
 * Sharing an FRN or FINIO firm name sends one firm's bulk valuations to another, so the API
 * refuses it until confirmed. `submitWithConfirmation` makes the request, and if the API
 * reports a shared identifier, shows which companies already use it and resubmits on confirm.
 * Resolves to whether the request was made successfully.
 */
export function useSharedFeedIdentifiersConfirmation() {
  const [pending, setPending] = useState<PendingConfirmation | null>(null);

  const submitWithConfirmation = async (
    submit: (confirmSharedFeedIdentifiers: boolean) => Promise<unknown>,
  ): Promise<boolean> => {
    try {
      await submit(false);
      return true;
    } catch (error) {
      const matches = getSharedFeedMatches(error);
      if (!matches) throw error;

      const confirmed = await new Promise<boolean>(resolve => setPending({ matches, resolve }));
      setPending(null);
      if (!confirmed) return false;

      await submit(true);
      return true;
    }
  };

  const confirmationModal = (
    <Modal
      opened={pending !== null}
      onClose={() => pending?.resolve(false)}
      title="Share bulk valuations?"
    >
      <Stack gap="md">
        <Text size="sm">
          Another company already uses this. If you continue, both companies will receive its bulk
          valuations.
        </Text>
        <List size="sm">
          {pending?.matches.map(match => (
            <List.Item key={`${match.identifier_type}-${match.identifier}-${match.company_name}`}>
              {IDENTIFIER_LABELS[match.identifier_type]} <strong>{match.identifier}</strong> is used
              by <strong>{match.company_name}</strong>
            </List.Item>
          ))}
        </List>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => pending?.resolve(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={() => pending?.resolve(true)}>
            Share
          </Button>
        </Group>
      </Stack>
    </Modal>
  );

  return { submitWithConfirmation, confirmationModal };
}
